import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaPlus,
  FaExternalLinkAlt,
  FaSearch,
  FaSyncAlt,
  FaBoxOpen,
  FaEuroSign,
  FaLayerGroup,
  FaLink,
} from "react-icons/fa";
import DOMPurify from "dompurify";
import ButtonEdit from "../../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../../components/Button/ButtonDelete";
import {
  getAllProducts,
  deleteProductById,
} from "../../../../services/productsServices";
import useScrollToTop from "../../../../hooks/useScrollToTop";
import { ToastContainer, toast } from "react-toastify";
import AddProductModal from "./AddProductModal";
import EditProductModal from "./EditProductModal";

const CARD =
  "rounded-[26px] border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] backdrop-blur";
const HERO =
  "relative overflow-hidden rounded-[30px] bg-slate-950 text-white border border-slate-800 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]";
const INPUT =
  "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/40";
const BUTTON_SOFT =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition";

function formatMoney(value) {
  return Number(value || 0).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
}

function htmlToPlainText(html = "") {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
  return clean.replace(/\s+/g, " ").trim();
}

function truncate(str = "", max = 140) {
  if (!str) return "—";
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
}

function StatCard({ icon, label, value, hint, dark = false }) {
  return (
    <div
      className={
        dark
          ? "rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur-md"
          : "rounded-[22px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`text-xs uppercase tracking-[0.18em] ${
              dark ? "text-white/60" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {label}
          </div>
          <div
            className={`mt-2 text-2xl font-semibold ${
              dark ? "text-white" : "text-slate-900 dark:text-slate-50"
            }`}
          >
            {value}
          </div>
          {hint ? (
            <div
              className={`mt-1 text-xs ${
                dark ? "text-white/70" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {hint}
            </div>
          ) : null}
        </div>

        <div
          className={`rounded-2xl p-3 ${
            dark
              ? "bg-white/10 text-white"
              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, description, action }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
            {eyebrow}
          </div>
        ) : null}
        <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    green:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
    orange:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-200 ${className}`}>
      {children}
    </td>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl dark:bg-slate-800">
        📦
      </div>
      <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        Aucun produit trouvé
      </div>
      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Ajoute un produit ou élargis ta recherche.
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="p-5 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

function MobileInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 p-3">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 font-medium text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

const ListingProducts = () => {
  useScrollToTop();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedPeriodicity, setSelectedPeriodicity] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProductId, setEditProductId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const productsData = await getAllProducts();
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error("Erreur lors du chargement des produits:", error);
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.categorie_product) set.add(p.categorie_product);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [products]);

  const periodicityOptions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.periodicite_product) set.add(p.periodicite_product);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [products]);

  const displayedProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let rows = [...products];

    if (term) {
      rows = rows.filter((product) => {
        const haystack = [
          product.nom_product,
          product.categorie_product,
          product.periodicite_product,
          htmlToPlainText(product.contenu_product || ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(term);
      });
    }

    if (selectedCategory) {
      rows = rows.filter(
        (product) => product.categorie_product === selectedCategory
      );
    }

    if (selectedPeriodicity) {
      rows = rows.filter(
        (product) => product.periodicite_product === selectedPeriodicity
      );
    }

    rows.sort((a, b) => {
      switch (sortBy) {
        case "price_desc":
          return Number(b.tarif_product || 0) - Number(a.tarif_product || 0);
        case "price_asc":
          return Number(a.tarif_product || 0) - Number(b.tarif_product || 0);
        case "category_asc":
          return String(a.categorie_product || "").localeCompare(
            String(b.categorie_product || ""),
            "fr"
          );
        case "name_desc":
          return String(b.nom_product || "").localeCompare(
            String(a.nom_product || ""),
            "fr"
          );
        case "name_asc":
        default:
          return String(a.nom_product || "").localeCompare(
            String(b.nom_product || ""),
            "fr"
          );
      }
    });

    return rows;
  }, [products, searchTerm, selectedCategory, selectedPeriodicity, sortBy]);

  const stats = useMemo(() => {
    const withStripe = displayedProducts.filter(
      (p) => !!p.payment_stripe_link
    ).length;

    const totalRevenue = displayedProducts.reduce(
      (sum, p) => sum + Number(p.tarif_product || 0),
      0
    );

    const avgPrice = displayedProducts.length
      ? totalRevenue / displayedProducts.length
      : 0;

    return {
      total: displayedProducts.length,
      withStripe,
      categories: new Set(
        displayedProducts.map((p) => p.categorie_product).filter(Boolean)
      ).size,
      avgPrice,
    };
  }, [displayedProducts]);

  const deleteProduct = async (productId) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer ce produit ? ID: ${productId}`
      )
    ) {
      return;
    }

    try {
      const message = await deleteProductById(productId);
      toast.success(message || "Produit supprimé avec succès");
      setProducts((prev) => prev.filter((product) => product.id !== productId));
    } catch (error) {
      console.error("Erreur lors de la suppression du produit:", error);
      toast.error("Erreur lors de la suppression du produit");
    }
  };

  const openEditModal = (productId) => {
    setEditProductId(productId);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditProductId(null);
  };

  const isEmpty = !loading && displayedProducts.length === 0;

  return (
    <div className="space-y-6 p-4 lg:p-6">
      {/* HERO */}
      <section className={HERO}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(129,140,248,0.35),transparent_30%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.30),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.16),transparent_30%)]" />
        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                <FaBoxOpen />
                Catalogue & vente
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Gestion des produits
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 md:text-base">
                Consulte, filtre et gère tous tes produits commercialisés avec
                leurs contenus, tarifs, périodicités et liens de paiement.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className={`${BUTTON_SOFT} bg-white text-slate-900 hover:bg-slate-100`}
              >
                <FaPlus />
                Ajouter un produit
              </button>

              <button
                type="button"
                onClick={fetchData}
                className={`${BUTTON_SOFT} bg-white/10 text-white hover:bg-white/20 border border-white/15`}
              >
                <FaSyncAlt />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              dark
              icon={<FaBoxOpen />}
              label="Produits"
              value={stats.total}
              hint="Résultats affichés"
            />
            <StatCard
              dark
              icon={<FaLink />}
              label="Liens Stripe"
              value={stats.withStripe}
              hint="Produits avec paiement"
            />
            <StatCard
              dark
              icon={<FaLayerGroup />}
              label="Catégories"
              value={stats.categories}
              hint="Diversité du catalogue"
            />
            <StatCard
              dark
              icon={<FaEuroSign />}
              label="Panier moyen"
              value={formatMoney(stats.avgPrice)}
              hint="Tarif moyen affiché"
            />
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Filtres"
          title="Retrouver rapidement un produit"
          description="Recherche par nom, catégorie, périodicité ou contenu."
        />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
          <div className="relative xl:col-span-5">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${INPUT} pl-11`}
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="xl:col-span-2">
            <select
              className={INPUT}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Toutes les catégories</option>
              {categoryOptions.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="xl:col-span-2">
            <select
              className={INPUT}
              value={selectedPeriodicity}
              onChange={(e) => setSelectedPeriodicity(e.target.value)}
            >
              <option value="">Toutes les périodicités</option>
              {periodicityOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="xl:col-span-2">
            <select
              className={INPUT}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name_asc">Tri : Nom A → Z</option>
              <option value="name_desc">Tri : Nom Z → A</option>
              <option value="price_desc">Tri : Prix décroissant</option>
              <option value="price_asc">Tri : Prix croissant</option>
              <option value="category_asc">Tri : Catégorie</option>
            </select>
          </div>

          <div className="xl:col-span-1">
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("");
                setSelectedPeriodicity("");
                setSortBy("name_asc");
              }}
              className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-200 transition hover:bg-slate-200 dark:hover:bg-slate-700"
            >
              Reset
            </button>
          </div>
        </div>
      </section>

      {/* LIST */}
      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-slate-200 dark:border-slate-800 px-5 py-4 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Catalogue produits
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Liste complète des offres avec aperçu du contenu et actions de
                gestion.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="purple">{displayedProducts.length} affiché(s)</Badge>
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonList />
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-[1250px] w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <Th>Produit</Th>
                    <Th>Contenu</Th>
                    <Th>Tarif</Th>
                    <Th>Périodicité</Th>
                    <Th>Catégorie</Th>
                    <Th>Stripe</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>

                <tbody>
                  {displayedProducts.map((product) => (
                    <tr
                      key={product.id}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition"
                    >
                      <Td className="min-w-[220px]">
                        <div className="font-semibold text-slate-900 dark:text-slate-50">
                          {product.nom_product}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          #{product.id}
                        </div>
                      </Td>

                      <Td className="min-w-[330px] max-w-[380px]">
                        <div className="leading-6 text-slate-600 dark:text-slate-300">
                          {truncate(htmlToPlainText(product.contenu_product || ""), 180)}
                        </div>
                      </Td>

                      <Td className="min-w-[120px] font-semibold text-slate-900 dark:text-slate-50">
                        {formatMoney(product.tarif_product)}
                      </Td>

                      <Td className="min-w-[150px]">
                        <Badge tone="blue">
                          {product.periodicite_product || "—"}
                        </Badge>
                      </Td>

                      <Td className="min-w-[160px]">
                        <Badge tone="green">
                          {product.categorie_product || "—"}
                        </Badge>
                      </Td>

                      <Td className="min-w-[120px]">
                        {product.payment_stripe_link ? (
                          <a
                            href={product.payment_stripe_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
                          >
                            <FaExternalLinkAlt />
                            Ouvrir
                          </a>
                        ) : (
                          <span className="text-sm text-slate-400">Pas de lien</span>
                        )}
                      </Td>

                      <Td className="min-w-[110px]">
                        <div className="flex justify-end gap-2">
                          <ButtonEdit
                            onClick={() => openEditModal(product.id)}
                            hoverLabel="Modifier"
                          />
                          <ButtonDelete
                            onClick={() => deleteProduct(product.id)}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile */}
            <div className="grid gap-4 p-4 md:hidden">
              {displayedProducts.map((product) => (
                <div
                  key={product.id}
                  className="rounded-[24px] border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        {product.nom_product}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        #{product.id}
                      </div>
                    </div>

                    <Badge tone="green">{product.categorie_product || "—"}</Badge>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <MobileInfo
                      label="Tarif"
                      value={formatMoney(product.tarif_product)}
                    />
                    <MobileInfo
                      label="Périodicité"
                      value={product.periodicite_product || "—"}
                    />
                    <MobileInfo
                      label="Contenu"
                      value={truncate(htmlToPlainText(product.contenu_product || ""), 150)}
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {product.payment_stripe_link ? (
                      <a
                        href={product.payment_stripe_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-500/10 dark:text-blue-300 dark:hover:bg-blue-500/20"
                      >
                        <FaExternalLinkAlt />
                        Stripe
                      </a>
                    ) : (
                      <span className="text-sm text-slate-400">Pas de lien Stripe</span>
                    )}

                    <div className="ml-auto flex gap-2">
                      <ButtonEdit
                        onClick={() => openEditModal(product.id)}
                        hoverLabel="Modifier"
                      />
                      <ButtonDelete
                        onClick={() => deleteProduct(product.id)}
                        hoverLabel="Supprimer"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <AddProductModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        refreshList={fetchData}
      />

      {showEditModal && editProductId && (
        <EditProductModal
          isOpen={showEditModal}
          onClose={closeEditModal}
          productId={editProductId}
          refreshList={fetchData}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default ListingProducts;